import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Drawer, List, IconButton, Grid, Box, Typography } from "@mui/material";
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import { createTheme, ThemeProvider, StyledEngineProvider, useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { MdOutlineChevronLeft, MdOutlineChevronRight } from "react-icons/md";
import { getCustomer, getTipologias } from "../../apis/backendRequests";
import { makeRequest } from "../../apis/makeRequest";
import { cartActions, galleryActions } from "../../actions";
import { DrawerItemsCatalogo } from "./DrawerItemsCatalogo";
import { useMediaQuery } from "@mui/material";

// Definir tema para Material UI
const theme = createTheme({
  palette: {
    mode: "dark",
  },
});

// Estilos para los componentes
const styles = {
  paper: (isMobile) => ({
    width: isMobile ? "100%" : "50%",
    height: isMobile ? "100%" : "40%",
    backgroundColor: "white",
    opacity: 0.9,
    color: "black",
    scrollbarWidth: "none",
  }),
  gridCloseBtn: {
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
  },
};

// Componente para Drawer en dispositivos móviles
function DrawerTipologiaCatalogoMobile({ dataCategories, setDrawerOpen, drawerOpen, isMobile, tabs, changeCategoria }) {
  return (
    <>
      <IconButton
        onClick={() => setDrawerOpen(true)}
        disabled={Object.keys(dataCategories).length === 0}
      >
        <MenuOutlinedIcon fontSize="large" />
      </IconButton>

      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Drawer
            anchor="left"
            elevation={1}
            open={drawerOpen}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") setDrawerOpen(false);
            }}
            PaperProps={{ style: styles.paper(isMobile) }}
          >
            <Grid container maxWidth="xxxl" paddingX="20px" sx={{ position: "relative" }}>
              <Grid item xs={12} display="flex" justifyContent="space-between" sx={styles.gridCloseBtn}>
                <Typography variant="h6" color="black" sx={{ flexGrow: 1 }}>
                  Categorías
                </Typography>
                <IconButton onClick={() => setDrawerOpen(false)}>
                  <MdOutlineChevronLeft style={{ color: "black", fontSize: "25px", transition: "ease-in-out 1s all" }} />
                </IconButton>
              </Grid>

              <Grid item xs={12}>
                <List>
                  {tabs.nameTabs.map((tab) => (
                    <DrawerItemsCatalogo
                      label={tab.label}
                      key={tab.label}
                      setCategorias={changeCategoria}
                      categorias={tabs.categories}
                      setDrawerOpen={setDrawerOpen}
                    />
                  ))}
                </List>
              </Grid>
            </Grid>
          </Drawer>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}

// Componente principal del Drawer
export default function DrawerTipologiaCatalogo(props) {
  const { isLoggedIn } = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dataCategories, setDataCategories] = useState([]);
  const [tabs, setTabs] = useState({ nameTabs: [], categories: [] });

  const { brand } = useSelector((state) => state.gallery);
  const { dynamicsId } = useSelector((state) => state.account);
  const { webOrderId } = useSelector((state) => state.cart);

  const allStr = "Todas";

  // Efecto para obtener categorías de la API al cargar
  useEffect(() => {
    makeRequest(
      () => getCustomer(isLoggedIn ? dynamicsId : "online"),
      (res) => {
        if (res.data.priceLevel) {
          makeRequest(
            () => getTipologias(res.data.priceLevel),
            (request) => {
              if (Object.keys(request.data).length > 0) {
                setDataCategories([{ name: allStr }, ...request.data]);
              }
            },
            null,
            { checkEmptyResults: false }
          );
        }
      }
    );
  }, [brand, dynamicsId, isLoggedIn]);

  // Efecto para organizar las categorías en tabs después de recibir las categorías
  useEffect(() => {
    if (Object.keys(dataCategories).length !== 0) {
      const tab = dataCategories.reduce((ac, categoria) => {
        if (categoria.categoriaDescription) {
          const tipo = categoria.categoriaDescription;
          if (!ac[tipo]) ac[tipo] = {};
          const rubro = categoria.rubroDescription;
          if (!ac[tipo][rubro]) ac[tipo][rubro] = [];
          ac[tipo][rubro].push(categoria);
        }
        return ac;
      }, {});

      const categoryNames = ["Todos los productos", ...Object.keys(tab), "Lanzamientos", "Destacados"].map(
        (label) => ({ label })
      );
      setTabs({ nameTabs: categoryNames, categories: tab });
    }
  }, [dataCategories]);

  // Función para cambiar la categoría seleccionada
  const changeCategoria = (categoria = "", rubro = "", subRubro = "", promocion = "") => {
    dispatch(galleryActions.setSearchTerm(""));
    dispatch(galleryActions.setCategoria(categoria));
    dispatch(galleryActions.setRubro(rubro));
    dispatch(galleryActions.setSubRubro(subRubro));
    dispatch(galleryActions.setPromocion(promocion === "Lanzamientos" ? "Lanzamient" : promocion));
    if (location.pathname !== "/") {
      if (webOrderId !== null) dispatch(cartActions.clear());
      navigate("/");
    }
  };

  // Si es móvil, renderizar el Drawer móvil
  if (isMobile) {
    return (
      <DrawerTipologiaCatalogoMobile
        dataCategories={dataCategories}
        setDrawerOpen={setDrawerOpen}
        drawerOpen={drawerOpen}
        isMobile={isMobile}
        tabs={tabs}
        changeCategoria={changeCategoria}
      />
    );
  }

  return (
    <>
      <IconButton
        onClick={() => setDrawerOpen(true)}
        disabled={Object.keys(dataCategories).length === 0}
      >
        <MenuOutlinedIcon fontSize="large" />
      </IconButton>

      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Drawer
            anchor="left"
            elevation={1}
            open={drawerOpen}
            onClose={(e, reason) => {
              setDrawerOpen(false);
            }}
            PaperProps={{ style: { ...styles.paper(false) } }}
            ModalProps={{
              slotProps: {
                backdrop: {
                  invisible: true,
                },
              },
            }}
          >
            <Grid container maxWidth="xxxl" paddingX="20px" sx={{ position: "relative" }}>
              <Grid item sm={1} sx={styles.gridCloseBtn}>
                <IconButton onClick={() => setDrawerOpen(false)}>
                  {drawerOpen ? (
                    <MdOutlineChevronLeft style={{ color: "black", fontSize: "25px", transition: "ease-in-out 1s all" }} />
                  ) : (
                    <MdOutlineChevronRight style={{ color: "black", fontSize: "25px", transition: "ease-in-out 1s all" }} />
                  )}
                </IconButton>
              </Grid>

              <Grid item sm={11} display="flex" justifyContent="space-evenly">
                {[0, 1].map((col) => (
                  <List key={col} sx={{ width: "50%" }}>
                    {tabs.nameTabs
                      .filter((_, i) => i % 2 === col)
                      .map((tab) => (
                        <DrawerItemsCatalogo
                          label={tab.label}
                          key={tab.label}
                          setCategorias={changeCategoria}
                          categorias={tabs.categories}
                          setDrawerOpen={setDrawerOpen}
                        />
                      ))}
                  </List>
                ))}
              </Grid>
            </Grid>
          </Drawer>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}
